import { initializeApp } from "firebase/app";
import {
  initializeAuth, GoogleAuthProvider, OAuthProvider,
  browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence,
  browserPopupRedirectResolver, connectAuthEmulator,
} from "firebase/auth";
import { getFirestore, initializeFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getAnalytics, setAnalyticsCollectionEnabled, setUserProperties } from "firebase/analytics";
// Not a real package: vite.config.js aliases this specifier to ./appCheckInit.js
// when VITE_RECAPTCHA_SITE_KEY is set and to ./appCheckInit.off.js otherwise, so
// a key-less build carries no App Check SDK (vitest.config.js: always off).
import { initAppCheck } from "sendly-app-check-init";
import { USE_EMULATORS, EMULATOR_HOST, EMULATOR_PORTS } from "./emulators";
import { localStore } from "./utils/safeStorage";

// `import.meta.env.VITE_*` is replaced at build time by Vite; only VITE_-prefixed
// keys reach the bundle. A production build refuses to run without the first
// four (vite.config.js guards them), because a missing key is a white screen.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FB_API_KEY,
  authDomain: import.meta.env.VITE_FB_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FB_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FB_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FB_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FB_APP_ID,
  measurementId: import.meta.env.VITE_FB_MEASUREMENT_ID,
};

export const app = initializeApp(firebaseConfig);

// App Check — reCAPTCHA Enterprise. Synchronous, right after initializeApp and
// before Firestore's first use (a token-less first request would be refused
// when enforcement is on). What runs here is decided at build time by the
// alias above; see src/appCheckInit.js.
initAppCheck(app);

// ── Auth persistence is DECLARED here, never switched afterwards ──
//
// This used to be `getAuth(app)` followed by
// `setPersistence(auth, browserLocalPersistence)`. Both lines were needed —
// `getAuth()` initialises with the chain [indexedDB, localStorage, session], so
// IndexedDB wins, while useAuth's auth-timeout heuristic scans localStorage for
// a `firebase:authUser:*` key — but the combination signed users out.
//
// `PersistenceUserManager.setPersistence()` migrates remove-FIRST:
//     const currentUser = await this.getCurrentUser();
//     await this.removeCurrentUser();      // ← user is now in NO store
//     this.persistence = newPersistence;
//     if (currentUser) return this.setCurrentUser(currentUser);
// and by then the cross-tab listener is already attached (it goes on in the
// manager's constructor, during init). The init-time migration inside
// `PersistenceUserManager.create()` does the opposite — set, then remove, and
// all of it before any listener exists — so switching after the fact was
// deliberately taking the worse of the SDK's two migration paths, on every
// single boot.
//
// With one tab nobody observes the gap. With SEVERAL tabs it is fatal: a
// service-worker update fires `controllerchange` in every controlled tab
// (index.html), so they all reload at the same instant and all run this
// migration concurrently. Firebase delivers cross-tab auth changes by POLLING
// (1000 ms for IndexedDB, 800 ms for localStorage), so one tab's poll lands
// inside another tab's remove→set gap, reads null, and `_onStorageEvent` takes
// its "either a new login or logout" branch → `_updateCurrentUser(null)` → a
// real sign-out, written back to the store and propagated to every other tab.
// Reported from production: an update accepted on /profile with several tabs
// open bounced the user to the gym selector, signed out.
//
// Declaring the hierarchy at init removes the window entirely. localStorage
// leads (so useAuth's `firebase:authUser:*` scan is reliable — the old order
// had `create()` deleting that copy at init and this line re-adding it a tick
// later, and a boot reading in between picked the 8s auth timeout instead of
// 30s, the "tablet signed me out" symptom). IndexedDB stays in the hierarchy so
// a session still living there is found and migrated via the SAFE path, which
// is what keeps this change from logging everyone out once.
//
// `popupRedirectResolver` MUST be passed explicitly: `getAuth()` adds it for
// you, `initializeAuth()` does not, and Google/Apple sign-in breaks without it.
// `browserSessionPersistence` last only to keep the old chain's final fallback:
// it is used solely when neither localStorage nor IndexedDB is available at all
// (locked-down private browsing), exactly as before.
export const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Browser E2E only (`.env.e2e`, see ./emulators.js). Connecting does not touch
// the persistence chain declared above — it only changes where the SDK sends
// its requests — so the "never switch persistence after init" rule still holds.
if (USE_EMULATORS) {
  connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, { disableWarnings: true });
}

// Override authDomain with current hostname so Google sign-in popup
// shows the real domain instead of the Firebase default (.firebaseapp.com).
// Only on production custom domains — skip localhost, IPs, and Firebase domains.
const _h = typeof window !== "undefined" && window.location.hostname;
if (_h && _h !== "localhost" && !/^[\d.:]+$/.test(_h)
    && !_h.endsWith(".web.app") && !_h.endsWith(".firebaseapp.com")) {
  auth.config.authDomain = _h;
}
export const googleProvider = new GoogleAuthProvider();

// Sign in with Apple. Requires same-origin `authDomain` (set above) so the
// redirect flow survives iOS standalone PWA — see IMPL-apple-signin.md.
// `name` is only returned on the user's FIRST authorization; Firebase persists
// it onto the user record, so the normal displayName flow in useAuth captures it.
export const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");
// ── Firestore initialization ──
//
// History — every persistence experiment so far broke iPad. Memory-only is
// the proven working configuration:
//
//  • commit cf40955 (Mar 25)  — `persistentLocalCache + persistentMultipleTabManager()`.
//                               Web Locks API deadlock on iPad Safari / Chrome iOS.
//  • commit 6f0ac62 (Mar 27)  — reverted, plain `getFirestore(app)` (memory-only).
//  • commit 234bad1 (Mar 30)  — same memory-only, but with
//                               `experimentalForceLongPolling` for PWA standalone
//                               (iOS kills WebSockets when the PWA backgrounds).
//  • commit f4c9728 (PR #2)   — tried `persistentLocalCache + persistentSingleTabManager()`,
//                               theory being the single-tab variant skipped the Web Locks
//                               path that broke before. Validated locally; in production
//                               iPad still failed to load gym data in Safari AND PWA.
//                               REVERTED in this commit.
//
// So: Firestore persistence stays OFF on every platform until we identify a
// configuration that actually works on iPad. The durability story for the
// competition flow does NOT depend on Firestore persistence — both the judge
// queue (`src/utils/judgeQueue.js`, ops "event" and "revoke") and the JAM
// queue (op "jam") write through our OWN IndexedDB store, which is stable on
// iPad (single object store, no Web Locks, no Firestore-SDK coupling). What
// we lose by reverting is only the "boot from cache instantly" UX nicety.
//
// `experimentalForceLongPolling` stays on for PWA standalone — iOS kills
// WebSocket connections when the PWA is backgrounded (e.g. during the Google
// login popup). HTTP long-polling is stateless and iOS cannot kill it.
//
// NEVER REINTRODUCE `persistentLocalCache(...)` without first verifying iPad
// Safari + Chrome iOS work end-to-end (login → load gym → switch gym → PWA
// standalone). The "looks fine locally" check is NOT sufficient.
const _isStandalone = typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator?.standalone === true);
export const db = _isStandalone
  ? initializeFirestore(app, { experimentalForceLongPolling: true })
  : getFirestore(app);

// Browser E2E only. The same memory-only instance — no cache option is added here.
if (USE_EMULATORS) {
  connectFirestoreEmulator(db, EMULATOR_HOST, EMULATOR_PORTS.firestore);
}

// Analytics — initialized lazily, ONLY after cookie consent is granted.
// We deliberately do NOT call getAnalytics() on boot: that injects gtag.js
// immediately (a network request to googletagmanager.com) even with collection
// disabled, which (a) loads a tracker before the user consents — GDPR-unfriendly
// — and (b) logs a console error for users whose ad-blocker / DNS filter blocks
// GTM (net::ERR_ADDRESS_INVALID). enableAnalytics() is the single entry point and
// is called by CookieConsent only when consent is "accepted" (on the accept
// click, and on mount for returning consented users).
let _analytics = null;
function ensureAnalytics() {
  if (!_analytics) {
    _analytics = getAnalytics(app);
    setAnalyticsCollectionEnabled(_analytics, false);
  }
  return _analytics;
}

export function enableAnalytics() {
  const analytics = ensureAnalytics();
  setAnalyticsCollectionEnabled(analytics, true);
  // Sync user properties on consent grant. `display_mode` is set here rather
  // than at sign-in because it belongs to this page load, signed in or not —
  // and it is the only install signal on iOS, which has no `appinstalled`.
  try {
    const lang = localStore.get("sendly-lang") || "hu";
    const theme = localStore.get("sendly-theme") || "light";
    setUserProperties(analytics, {
      preferred_language: lang,
      theme_preference: theme,
      display_mode: _isStandalone ? "standalone" : "browser",
    });
  } catch (e) { /* silent */ }
}
